import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

/**
 * A self-contained modal for the Hermes download dialogs.
 *
 * Deliberately not react-bootstrap's `Modal`. Those dialogs rendered a stray
 * unlabelled box between the title and the body which could not be traced to any
 * element in our own markup — the header held exactly a title and a close
 * button, and the body's first child was a paragraph. The app layers several
 * `.modal-*` overrides on top of Bootstrap 4, so the box came from markup or CSS
 * we do not control. Owning the whole structure removes that class of problem
 * rather than guessing at which rule caused it.
 *
 * Keeps the behaviours the Bootstrap version gave us:
 *   - a backdrop that does NOT dismiss on click (these dialogs spend credits, so
 *     an accidental click-through should not cancel or confirm anything)
 *   - Escape to close, unless the dialog is busy
 *   - body scroll locked while open
 *   - focus moved into the dialog, and aria wiring for the title
 */

type HermesDialogProps = {
  title: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  onClose: () => void;
  /** While true the dialog cannot be dismissed — work is in flight. */
  busy?: boolean;
  /** Overrides the dialog's max width (defaults to a compact tw-max-w-md) for content that needs more room, e.g. a two-column form. */
  widthClassName?: string;
};

export default function HermesDialog({ title, children, footer, onClose, busy = false, widthClassName = "tw-max-w-md" }: HermesDialogProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    // Prevent the page behind the dialog scrolling under it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [busy, onClose]);

  // Rendered on the page body, not where the caller sits: inside the results table or a card the
  // overlay would be held under that container's stacking level, and the app header and sidebar
  // would paint over it instead of being dimmed with the rest of the screen.
  return createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-[1055] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="hermes-dialog-title" className={`tw-w-full ${widthClassName} tw-rounded-xl tw-bg-white tw-shadow-xl`}>
        <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-gray-100 tw-px-5 tw-py-4">
          <h2 id="hermes-dialog-title" className="tw-m-0 tw-text-base tw-font-semibold tw-text-gray-900">
            {title}
          </h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="tw-ml-auto tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-border-none tw-bg-transparent tw-text-gray-400 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-transition-colors tw-cursor-pointer disabled:tw-opacity-40 disabled:tw-cursor-not-allowed">
            <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 -960 960 960" width="18" fill="currentColor">
              <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
            </svg>
          </button>
        </div>

        <div className="tw-px-5 tw-py-4">{children}</div>

        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-gray-100 tw-px-5 tw-py-3">{footer}</div>
      </div>
    </div>,
    document.body
  );
}
